import * as THREE from 'three';
import { SK } from './skin';

// The 3P WORLD-TRUTH RETICLE (owner 2026-07-13 aim overhaul, items #1/#2/#4/#7). A small dot drawn at
// the shot's PREDICTED IMPACT (drop + recoil included -- main computes it with the same math the bullet
// flies), a ring sized to the live spread cone at that distance, a bad-red tint when the muzzle path is
// blocked, and brief hitmarkers (normal / headshot) on landed damage. Pure DOM at HUD z; pointer-events
// none. Hidden in first person (hitmarkers fall back to screen centre there).

const RETICLE = {
  dotPx: 6,        // dot diameter
  ringMinPx: 12,   // never collapse the ring below this (readability)
  ringMaxPx: 260,  // sanity cap (a huge cone at close range shouldn't swallow the screen)
  hitLifeMs: 240,  // hitmarker flash duration
  headLifeMs: 380, // headshot marker lingers a touch longer
} as const;

export class Reticle {
  private readonly dot: HTMLDivElement;
  private readonly ring: HTMLDivElement;
  private readonly proj = new THREE.Vector3();
  private readonly proj2 = new THREE.Vector3();
  private readonly rightCol = new THREE.Vector3();
  private lastX = 0;
  private lastY = 0;
  private visible = false;

  constructor() {
    this.dot = document.createElement('div');
    this.dot.style.cssText = [
      'position:fixed', 'z-index:9', 'pointer-events:none', 'display:none',
      `width:${RETICLE.dotPx}px`, `height:${RETICLE.dotPx}px`, 'border-radius:50%',
      `background:${SK.textHi}`, 'box-shadow:0 0 4px rgba(0,0,0,0.9)', 'transform:translate(-50%,-50%)',
    ].join(';');
    this.ring = document.createElement('div');
    this.ring.style.cssText = [
      'position:fixed', 'z-index:9', 'pointer-events:none', 'display:none',
      'border:1.5px solid rgba(240,231,214,0.55)', 'border-radius:50%', 'transform:translate(-50%,-50%)',
      'box-shadow:0 0 3px rgba(0,0,0,0.6), inset 0 0 3px rgba(0,0,0,0.6)',
    ].join(';');
    document.body.append(this.ring, this.dot);
  }

  // Place the reticle at a world point. ringWorld = the spread radius in METRES at that point (0 hides
  // the ring); blocked paints everything bad-red. cam projects world -> screen.
  show(cam: THREE.PerspectiveCamera, point: THREE.Vector3, ringWorld: number, blocked: boolean): void {
    this.proj.copy(point).project(cam);
    if (this.proj.z > 1 || this.proj.z < -1) { this.hide(); return; }
    const w = window.innerWidth, h = window.innerHeight;
    const x = (this.proj.x * 0.5 + 0.5) * w;
    const y = (-this.proj.y * 0.5 + 0.5) * h;
    this.lastX = x; this.lastY = y; this.visible = true;
    this.dot.style.display = 'block';
    this.dot.style.left = `${x.toFixed(1)}px`;
    this.dot.style.top = `${y.toFixed(1)}px`;
    this.dot.style.background = blocked ? SK.bad : SK.textHi;
    if (ringWorld > 0.001) {
      // Project a point offset one spread-radius along the camera's right axis; the pixel gap = ring radius.
      this.rightCol.setFromMatrixColumn(cam.matrixWorld, 0);
      this.proj2.copy(point).addScaledVector(this.rightCol, ringWorld).project(cam);
      const rx = ((this.proj2.x * 0.5 + 0.5) * w) - x;
      const rPx = Math.min(RETICLE.ringMaxPx, Math.max(RETICLE.ringMinPx, Math.abs(rx)));
      this.ring.style.display = 'block';
      this.ring.style.left = `${x.toFixed(1)}px`;
      this.ring.style.top = `${y.toFixed(1)}px`;
      this.ring.style.width = `${(rPx * 2).toFixed(0)}px`;
      this.ring.style.height = `${(rPx * 2).toFixed(0)}px`;
      this.ring.style.borderColor = blocked ? SK.bad : 'rgba(240,231,214,0.55)';
    } else {
      this.ring.style.display = 'none';
    }
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.dot.style.display = 'none';
    this.ring.style.display = 'none';
  }

  // Flash a hitmarker (X) at the reticle -- or screen centre when the reticle is hidden (first person).
  // head = the headshot variant (bad-red, bigger, lingers).
  hit(head: boolean): void {
    const x = this.visible ? this.lastX : window.innerWidth / 2;
    const y = this.visible ? this.lastY : window.innerHeight / 2;
    const m = document.createElement('div');
    const size = head ? 22 : 16;
    const col = head ? SK.bad : SK.textHi;
    m.style.cssText = [
      'position:fixed', 'z-index:10', 'pointer-events:none',
      `left:${x.toFixed(1)}px`, `top:${y.toFixed(1)}px`, `width:${size}px`, `height:${size}px`,
      'transform:translate(-50%,-50%) rotate(45deg)',
    ].join(';');
    // Four ticks of an X (gapped cross rotated 45deg) -- reads at a glance without covering the target.
    for (const [l, t, w2, h2] of [[0, 0.5, 0.32, 0.12], [0.68, 0.5, 0.32, 0.12], [0.5, 0, 0.12, 0.32], [0.5, 0.68, 0.12, 0.32]] as const) {
      const seg = document.createElement('div');
      seg.style.cssText = `position:absolute;left:${l * 100}%;top:${t * 100}%;width:${w2 * 100}%;height:${h2 * 100}%;background:${col};box-shadow:0 0 3px rgba(0,0,0,0.8);transform:translate(-50%,-50%)`;
      m.appendChild(seg);
    }
    document.body.appendChild(m);
    const anim = m.animate(
      [{ opacity: 1, transform: 'translate(-50%,-50%) rotate(45deg) scale(1.25)' },
       { opacity: 1, transform: 'translate(-50%,-50%) rotate(45deg) scale(1)', offset: 0.35 },
       { opacity: 0, transform: 'translate(-50%,-50%) rotate(45deg) scale(0.9)' }],
      { duration: head ? RETICLE.headLifeMs : RETICLE.hitLifeMs, easing: 'ease-out' },
    );
    anim.onfinish = () => m.remove();
    anim.oncancel = () => m.remove();
  }
}
